// src/components/TopNav/TopNav.js
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "antd";
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  BellOutlined,
  MailOutlined,
  DownOutlined,
  BgColorsOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import useAppTheme, { THEMES } from "../../hooks/useAppTheme";

const asPositiveInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

const TopNav = ({ toggleSidebar, isSidebarOpen, title }) => {
  const { authData, handleLogout } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useAppTheme();

  const displayName = useMemo(() => {
    if (authData?.firstName && authData?.lastName) return `${authData.firstName} ${authData.lastName}`;
    if (authData?.firstName) return authData.firstName;
    if (authData?.userName) return authData.userName;
    return "Lane Sands";
  }, [authData]);

  const primaryRole = useMemo(() => {
    if (Array.isArray(authData?.role) && authData.role.length) return authData.role[0];
    if (typeof authData?.role === "string") return authData.role;
    return "User";
  }, [authData]);

  const initials = useMemo(() => {
    const parts = displayName.trim().split(/\s+/);
    const a = parts[0]?.[0] ?? "";
    const b = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
    return (a + b).toUpperCase() || "LS";
  }, [displayName]);

  // Dots show only when the backend reports real unread counts.
  const showMailDot = asPositiveInt(authData?.mailUnreadCount) > 0;
  const showBellDot = asPositiveInt(authData?.notificationUnreadCount) > 0;

  const themeItems = Object.entries(THEMES).map(([key, def]) => ({
    key,
    label: (
      <span className="hms-theme-opt">
        <i className="hms-theme-swatch" style={{ background: def.dot }} />
        {def.label}
      </span>
    ),
  }));

  const userMenu = [
    { key: "profile", label: "Profile" },
    { key: "settings", label: "Settings" },
    { type: "divider" },
    { key: "signout", danger: true, label: "Sign out" },
  ];

  const onUserMenuClick = ({ key }) => {
    if (key === "profile") return navigate("/hotels/profilesettingspage");
    if (key === "settings") return navigate("/hotel/my/settings");
    if (key === "signout") return handleLogout();
    return undefined;
  };

  return (
    <div className="hms-topbar">
      <div className="hms-topbar-left">
        <button
          type="button"
          className="hms-icon-btn"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          {isSidebarOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
        </button>
        {title ? <div className="hms-page-title">{title}</div> : null}
      </div>

      <div className="hms-topbar-right">
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: themeItems,
            selectable: true,
            selectedKeys: [theme],
            onClick: ({ key }) => setTheme(key),
          }}
        >
          <button type="button" className="hms-icon-btn is-themed" aria-label="Change theme" title="Theme">
            <BgColorsOutlined />
          </button>
        </Dropdown>

        <button
          type="button"
          className="hms-icon-btn"
          onClick={() => navigate("/hotel/emails")}
          aria-label="Outbox logs"
        >
          <MailOutlined />
          {showMailDot && <span className="hms-badge-dot" />}
        </button>

        <button type="button" className="hms-icon-btn is-tint" aria-label="Notifications">
          <BellOutlined />
          {showBellDot && <span className="hms-badge-dot" />}
        </button>

        <Dropdown menu={{ items: userMenu, onClick: onUserMenuClick }} trigger={["click"]}>
          <div className="hms-user" role="button" tabIndex={0}>
            <div className="hms-avatar">{initials}</div>
            <div className="hms-user-meta">
              <div className="hms-user-name">{displayName}</div>
              <div className="hms-user-sub">
                <span>{primaryRole}</span>
                {authData?.hotelName ? <span className="hms-chip">{authData.hotelName}</span> : null}
              </div>
            </div>
            <DownOutlined className="hms-user-caret" />
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

export default TopNav;
